<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onDestroy, untrack } from 'svelte';
    import settingsKeys from '../../../settings/settingsKeys.js';
    import ControlRow from './generic/ControlRow.svelte';
    import { __ } from '@oat-sa-private/ui-core';

    /**
     * @typedef {Object} Props
     * @property {Object} areaBroker
     * @property {Object} initialState
     * @property {Function} onChange
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        areaBroker,
        initialState,
        onChange
    } = $props();

    // svelte-ignore state_referenced_locally
    const targetElt = areaBroker?.getContainer();

    // stepper props
    // these values represent multipliers for the default line-heights
    const min = 1.5;
    const options = [1.5, 1.6, 1.7, 1.8, 1.9, 2, 2.1, 2.2, 2.3, 2.4, 2.5];
    let value = $state(1.5);

    // from LDS styles
    const lineHeightBodyDefault = 1.5;
    const lineHeightHeadingDefault = 1.2;

    const initialStateOnce = untrack(() => initialState);
    if (initialStateOnce?.value) {
        handleLineHeightChange(initialStateOnce);
    }
    let nonDefault = initialStateOnce?.nonDefault;

    /**
     * Handle the input being changed
     * @param {Object} [newState]
     */
    function handleLineHeightChange(newState = {}) {
        value = newState?.value;
        const lineHeightMultiplier = value / min;
        const lineHeightBodyOperationValue = lineHeightBodyDefault * lineHeightMultiplier;
        const lineHeightBodyValue = parseFloat(lineHeightBodyOperationValue.toFixed(1));
        const lineHeightHeadingValue = lineHeightHeadingDefault * lineHeightMultiplier;

        if (targetElt) {
            if (value !== min) {
                targetElt.dataset.a11yOverrideLineHeight = 'true';
            } else {
                delete targetElt.dataset.a11yOverrideLineHeight;
            }

            targetElt.style.setProperty('--line-height-default', lineHeightBodyValue);
            targetElt.style.setProperty('--line-height-heading', lineHeightHeadingValue);
        }

        onChange?.({
            key: settingsKeys.lineHeight,
            state: {
                value,
                lineHeightBodyValue,
                nonDefault: newState?.nonDefault
            }
        });
    }

    onDestroy(() => {
        delete targetElt?.dataset.a11yOverrideLineHeight;

        targetElt?.style.removeProperty('--line-height-default');
        targetElt?.style.removeProperty('--line-height-heading');
    });
</script>

<ControlRow
    icon="font-line-height-16"
    label={__('Line height')}
    {options}
    {value}
    {nonDefault}
    ariaLabelDecr={__('decrease line height')}
    ariaLabelIncr={__('increase line height')}
    onChange={handleLineHeightChange} />
